import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Chip } from '@c2fo/liquidity';
import ErrorLoadingMarkets from '@/enterprise/components/ErrorLoadingMarkets';
import { MarketTypeSelectorItem } from '@/enterprise/components/MarketTypeSelector';
import Skeleton from '@/enterprise/components/Skeleton';
import useTakerMarkets from '@/enterprise/data/useTakerMarkets';
import useTakerMarketsGroups from '@/enterprise/data/useTakerMarketsGroups';
import TabActions from '@/enterprise/features/homepage/TabActions';
import NoAvailableMarkets from '@/enterprise/features/takerMarketTabs/NoAvailableMarkets';
import tabLinks from '@/enterprise/features/takerMarketTabs/tabLinks';
import LinkTabs from '@/shared/components/LinkTabs';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import useIsAccountQualifiedToNotifySrm from '@/utils/useIsAccountQualifiedToNotifySrm';
import useLocaleFormat from '@/utils/useLocaleFormat';

const getTabTitle = (translatedLabel: string, takerMarketCount: number) => {
  return takerMarketCount > 0 ? `${translatedLabel} (${takerMarketCount})` : translatedLabel;
};

const TakerMarketTabsComponent = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const data = useTakerMarketsGroups();
  const { asNumber } = useLocaleFormat();
  const { data: takerMarkets } = useTakerMarkets();
  const isAccountQualifiedToNotifySrm = useIsAccountQualifiedToNotifySrm();

  // Show preferred marketing tab to request more info if the supplier is a Sun or Sweet supplier and has preferred eligible markets
  const showPreferredMarketing =
    takerMarkets?.some((tm) => tm.marketFeatures.includes('PREFERRED_ELIGIBLE')) && isAccountQualifiedToNotifySrm;

  const { rollup: nyrRollup, takerMarkets: nyrTakerMarkets } = data['NAME_YOUR_RATE'];

  const tabItems: MarketTypeSelectorItem[] = [
    {
      key: 'NAME_YOUR_RATE',
      active: pathname === tabLinks.NAME_YOUR_RATE,
      amount: nyrRollup.amount,
      count: nyrRollup.count,
      label: getTabTitle(t('core.nameYourRate'), nyrRollup.count),
      link: tabLinks.NAME_YOUR_RATE,
      newDivisionCount: nyrRollup.newDivisionCount,
      takerMarkets: nyrTakerMarkets,
    },
  ];

  if (data['PREFERRED'].rollup.count > 0 || showPreferredMarketing) {
    const { rollup, takerMarkets } = data['PREFERRED'];
    tabItems.push({
      key: 'PREFERRED',
      active: pathname === tabLinks.PREFERRED,
      amount: rollup.amount,
      count: rollup.count,
      label: getTabTitle(t('core.preferredRate'), rollup.count),
      link: tabLinks.PREFERRED,
      newDivisionCount: rollup.newDivisionCount,
      takerMarkets,
    });
  }

  if (data['BENCHMARK'].rollup.count > 0) {
    const { rollup, takerMarkets } = data['BENCHMARK'];
    tabItems.push({
      key: 'BENCHMARK',
      active: pathname === tabLinks.BENCHMARK,
      amount: rollup.amount,
      count: rollup.count,
      label: getTabTitle(t('core.variableRate'), rollup.count),
      link: tabLinks.BENCHMARK,
      newDivisionCount: rollup.newDivisionCount,
      takerMarkets,
    });
  }

  if (data['FIXED'].rollup.count > 0) {
    const { rollup, takerMarkets } = data['FIXED'];
    tabItems.push({
      key: 'FIXED',
      active: pathname === tabLinks.FIXED,
      amount: rollup.amount,
      count: rollup.count,
      label: getTabTitle(t('core.fixedRate'), rollup.count),
      link: tabLinks.FIXED,
      newDivisionCount: rollup.newDivisionCount,
      takerMarkets,
    });
  }

  if (data['INACTIVE'].rollup.count > 0) {
    const { rollup, takerMarkets } = data['INACTIVE'];
    tabItems.push({
      key: 'INACTIVE',
      active: pathname === tabLinks.INACTIVE,
      amount: rollup.amount,
      count: rollup.count,
      label: getTabTitle(t('core.inactiveMarkets'), rollup.count),
      link: tabLinks.INACTIVE,
      newDivisionCount: rollup.newDivisionCount,
      takerMarkets,
    });
  }

  const activeTab = tabItems.find((item) => item.active)?.key ?? 'NAME_YOUR_RATE';

  // if the supplier does not have any associated taker markets then render a message
  // that displays there are no available markets and directs them to add customers
  if (!takerMarkets?.length) {
    return <NoAvailableMarkets />;
  }

  return (
    <div className="rounded-xl bg-white shadow">
      <div className="h-[58px] border-b-2 border-gray-100">
        <LinkTabs
          dataIntercomTarget="earlyPaymentMarkets"
          items={tabItems.map((item) => ({
            key: item.key,
            label:
              item.newDivisionCount > 0 ? (
                <span className="flex items-center gap-1">
                  <span>{item.label}</span>
                  <Chip
                    size="xs"
                    type="info"
                    variant="filled"
                    isRound
                    label={t('core.newWithCount', {
                      formattedCount: asNumber({ value: item.newDivisionCount }),
                    })}
                  />
                </span>
              ) : (
                item.label
              ),
            link: item.link,
            matcher: item.link.substring(1),
            matchExact: true,
          }))}
          variant="secondary"
        />
      </div>
      <div>
        <TabActions activeTab={activeTab} />
        <div className="overflow-y-hidden">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

const TakerMarketTabsFallback = () => (
  <div className="mt-6 flex flex-col gap-6 rounded-md bg-gray-100 p-4">
    <Skeleton className="h-10 w-full" />
    <Skeleton className="h-6 w-64" />
    <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
      <Skeleton className="h-20 w-full" />
      <Skeleton className="h-20 w-full" />
      <Skeleton className="h-20 w-full" />
      <Skeleton className="h-20 w-full" />
    </div>
    <Skeleton className="h-24 w-full" />
    <Skeleton className="h-24 w-full" />
    <Skeleton className="h-24 w-full" />
  </div>
);

const TakerMarketTabs = () => (
  <QueryBoundaries LoadingComponent={() => <TakerMarketTabsFallback />} ErrorComponent={() => <ErrorLoadingMarkets />}>
    <TakerMarketTabsComponent />
  </QueryBoundaries>
);

export default TakerMarketTabs;
